import * as Dialog from '@radix-ui/react-dialog';
import { Check, Copy, Plus, RotateCcw, ShieldCheck, Trash2, UserPlus } from 'lucide-react';
import { useState, type FormEvent } from 'react';

import { AdminPage } from '@/components/admin/AdminPage';
import { Button } from '@/components/ui/button';
import { DataTable, type Column } from '@/components/ui/data-table';
import { ConfirmDialog } from '@/components/ui/confirm-dialog';
import { EmptyState } from '@/components/ui/empty-state';
import { Field, FormError, SelectField } from '@/components/ui/field';
import { StatusBadge } from '@/components/ui/status-badge';
import {
  useAdminAccounts,
  useChangeAdminRole,
  useCreateAdminAccount,
  useDeleteAdminAccount,
  useDeletedAdminAccounts,
  useRestoreAdminAccount,
  useSetAdminAccountActive,
} from '@/hooks/admin-accounts';
import { ApiError } from '@/lib/api';
import { formatDay, formatInstant } from '@/lib/dates';
import { useToast } from '@/lib/toast-context';
import type { AdminAccountView, CreatedAdminAccount } from '@shared/schemas/admin-account';

/*
 * /admin/admins — who can get into this portal, and taking that away.
 * business-rules.md §2. Owner decision, 2026-08-06.
 *
 * **This screen was a placeholder from phase 2 until now**, and no ROADMAP
 * phase ever owned it: `create-user.js` over SSH was the only way to make an
 * admin, and there was no way to revoke one at all. The owner cannot run a
 * shell command, so both were blocked on somebody else.
 *
 * **Nothing here is access control.** Every button maps to an endpoint behind
 * `requireSuperAdmin`, and that is what refuses — CLAUDE.md §4. What this file
 * does is avoid offering a control that would only fail: an ordinary admin
 * never reaches this route at all, and a super admin does not see buttons on
 * their own row, because the server refuses those and the reason is worth
 * saying rather than discovering.
 */

export function AdminAccountsPage() {
  const accounts = useAdminAccounts();
  const [creating, setCreating] = useState(false);

  const rows = accounts.data?.data ?? [];

  const columns: Column<AdminAccountView>[] = [
    {
      key: 'name',
      header: 'Name',
      hideLabelOnCard: true,
      render: (row) => (
        <div className="min-w-0">
          <span className="flex items-center gap-1.5 text-body font-medium text-ink-900">
            {row.role === 'super_admin' ? (
              <ShieldCheck aria-hidden="true" className="size-4 shrink-0 text-bark-700" />
            ) : null}
            {row.name}
            {row.isYou ? <span className="text-meta text-ink-500">(you)</span> : null}
          </span>
          <span className="mt-0.5 block truncate text-meta text-ink-500">
            {row.email ?? row.username}
          </span>
        </div>
      ),
    },
    {
      key: 'role',
      header: 'Role',
      // The label, never `super_admin` — P5.
      render: (row) => <span className="text-body">{row.roleLabel}</span>,
    },
    {
      key: 'lastSignIn',
      header: 'Last signed in',
      render: (row) =>
        row.lastSignInAt === null ? (
          // Not a dash. "Never" is a fact worth reading on this screen in
          // particular — it is how you spot an account nobody uses (P1, P10).
          <span className="text-meta text-ink-500">Never</span>
        ) : (
          <span className="whitespace-nowrap text-body">{formatInstant(row.lastSignInAt)}</span>
        ),
    },
    {
      key: 'status',
      header: 'Status',
      render: (row) =>
        row.isActive ? (
          <span className="text-meta text-ink-500">Active</span>
        ) : (
          <StatusBadge label="No access" tone="cancelled" />
        ),
    },
    {
      key: 'actions',
      header: 'Actions',
      render: (row) => <RowActions row={row} />,
    },
  ];

  return (
    <AdminPage
      title="Admins"
      description="Who can get into this portal, and turning that off when someone leaves."
      actions={
        <Button
          onClick={() => {
            setCreating(true);
          }}
        >
          <Plus aria-hidden="true" />
          Add admin
        </Button>
      }
    >
      <p className="mb-4 max-w-prose text-meta text-ink-500">
        A super admin can do everything an admin can, plus four things: manage these accounts,
        change settings, set pay rates, and correct a clock-in or clock-out. Nobody can change their
        own role or remove their own access — that is what stops the last super admin locking
        everyone out.
      </p>

      <DataTable
        caption="Everyone who can reach the admin portal"
        columns={columns}
        rows={rows}
        rowKey={(row) => row.id}
        isLoading={accounts.isPending}
        // A failed request is an error, never an empty state — P22.
        error={accounts.error}
        onRetry={() => {
          void accounts.refetch();
        }}
        empty={
          <EmptyState
            icon={UserPlus}
            title="No admins yet."
            hint="That should not be possible — you are signed in as one."
          />
        }
      />

      <DeletedAdmins />

      <NewAdminDialog open={creating} onOpenChange={setCreating} />
    </AdminPage>
  );
}

/**
 * The recycle bin — owner decision, 2026-08-07.
 *
 * **Absent entirely when it is empty**, rather than an empty table with a
 * heading over it. A bin with nothing in it is not information, and a permanent
 * "no deleted accounts" panel is furniture on a screen somebody opens to do one
 * specific thing.
 *
 * It sits under the live list rather than behind a tab, because it is almost
 * always empty and a tab that is almost always empty is a tab nobody clicks —
 * which is exactly the wrong outcome for the one screen where somebody might be
 * looking for something they deleted by mistake.
 */
function DeletedAdmins() {
  const deleted = useDeletedAdminAccounts();
  const rows = deleted.data?.accounts ?? [];

  if (rows.length === 0) return null;

  return (
    <section className="mt-8">
      <h2 className="text-h3 text-ink-900">Deleted</h2>
      <p className="mb-3 mt-1 max-w-prose text-meta text-ink-500">
        These accounts cannot sign in. They are kept because they left something behind — a note, a
        message, or an entry in the audit trail — so that their name still appears against it. Each
        one is removed for good on the date shown.
      </p>

      <ul className="space-y-2">
        {rows.map((row) => (
          <DeletedRow key={row.id} row={row} />
        ))}
      </ul>
    </section>
  );
}

function DeletedRow({ row }: { row: AdminAccountView }) {
  const toast = useToast();
  const restore = useRestoreAdminAccount();

  return (
    <li className="flex flex-wrap items-center gap-x-3 gap-y-1 rounded-lg border border-ink-300 bg-white p-3 shadow-card">
      <span className="text-body font-medium text-ink-900">{row.name}</span>
      <span className="text-meta text-ink-500">{row.email ?? row.username}</span>
      <span className="text-meta text-ink-500">{row.roleLabel}</span>
      {row.purgesAt === null ? null : (
        <span className="text-meta text-ink-500">
          Removed for good on {formatDay(row.purgesAt.slice(0, 10))}
        </span>
      )}

      <Button
        variant="outline"
        size="sm"
        className="ml-auto"
        disabled={restore.isPending}
        onClick={() => {
          restore.mutate(row.id, {
            onSuccess: (account) => {
              /*
               * **Says what it did *not* do.** Restoring undoes the deletion,
               * not the loss of access — otherwise somebody clicking this to
               * recover a name would hand back a working login without meaning
               * to.
               */
              toast.success(
                `${account.name} is back. They still cannot sign in — use Restore access for that.`,
              );
            },
            onError: (cause) => {
              toast.error(
                cause instanceof ApiError ? cause.message : 'That account could not be restored.',
              );
            },
          });
        }}
      >
        <RotateCcw aria-hidden="true" className="size-4" />
        Restore
      </Button>
    </li>
  );
}

/**
 * The per-row controls.
 *
 * **Your own row has none, and says why.** The server refuses both actions on
 * yourself (it is the lockout guard), so rendering the buttons would mean
 * offering a click whose only outcome is an error.
 */
function RowActions({ row }: { row: AdminAccountView }) {
  const toast = useToast();
  const changeRole = useChangeAdminRole(row.id);
  const setActive = useSetAdminAccountActive(row.id);
  const remove = useDeleteAdminAccount();
  const [confirming, setConfirming] = useState(false);

  if (row.isYou) {
    return <span className="text-meta text-ink-500">Ask another super admin</span>;
  }

  const promoting = row.role === 'admin';

  return (
    <div className="flex flex-wrap gap-2">
      <Button
        variant="outline"
        size="sm"
        disabled={changeRole.isPending}
        onClick={() => {
          changeRole.mutate(promoting ? 'super_admin' : 'admin', {
            onSuccess: (account) => {
              toast.success(`${account.name} is now ${account.roleLabel.toLowerCase()}.`);
            },
          });
        }}
      >
        {promoting ? 'Make super admin' : 'Make admin'}
      </Button>

      <Button
        variant="outline"
        size="sm"
        disabled={setActive.isPending}
        onClick={() => {
          setActive.mutate(!row.isActive, {
            onSuccess: (account) => {
              toast.success(
                account.isActive
                  ? `${account.name} can sign in again.`
                  : `${account.name} can no longer sign in.`,
              );
            },
          });
        }}
      >
        {row.isActive ? 'Remove access' : 'Restore access'}
      </Button>

      {/*
        **Delete is destructive and sits apart from the other two.** Removing
        access is reversible with one click and reads as an ordinary decision;
        this one either bins the account for 30 days or removes it outright, and
        the confirm below is what makes that difference visible before it
        happens rather than after.
      */}
      <Button
        variant="outline"
        size="sm"
        className="text-status-priority-text"
        disabled={remove.isPending}
        onClick={() => {
          setConfirming(true);
        }}
      >
        <Trash2 aria-hidden="true" className="size-4" />
        Delete
      </Button>

      <ConfirmDialog
        open={confirming}
        onOpenChange={setConfirming}
        title={`Delete ${row.name}?`}
        /*
         * **The screen cannot know which of the two will happen** — whether an
         * account has left anything behind is a question about six tables, and
         * asking the server twice to find out would race with the answer. So
         * the copy describes both outcomes and the toast afterwards says which
         * one it was.
         */
        description="They lose access straight away. If they have done anything in the system their account goes to Deleted and can be restored for 30 days; if they have not, it is removed permanently."
        confirmLabel="Delete account"
        destructive
        isPending={remove.isPending}
        onConfirm={() => {
          remove.mutate(row.id, {
            onSuccess: (result) => {
              toast.success(
                result.mode === 'purged'
                  ? `${row.name} was removed permanently — they had no history.`
                  : `${row.name} was deleted. Restorable until ${formatDay(result.purgesAt!.slice(0, 10))}.`,
              );
              setConfirming(false);
            },
            onError: (cause) => {
              toast.error(
                cause instanceof ApiError ? cause.message : 'That account could not be deleted.',
              );
            },
          });
        }}
      />
    </div>
  );
}

function NewAdminDialog({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const create = useCreateAdminAccount();

  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [role, setRole] = useState<'admin' | 'super_admin'>('admin');
  const [handover, setHandover] = useState<CreatedAdminAccount | null>(null);

  const apiError = create.error instanceof ApiError ? create.error : null;

  function close() {
    setName('');
    setEmail('');
    setRole('admin');
    setHandover(null);
    create.reset();
    onOpenChange(false);
  }

  function submit(event: FormEvent) {
    event.preventDefault();
    create.mutate({ name, email, role }, { onSuccess: setHandover });
  }

  return (
    <Dialog.Root
      open={open}
      onOpenChange={(next) => {
        if (!next) close();
        else onOpenChange(true);
      }}
    >
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-40 bg-ink-900/40" />
        <Dialog.Content className="fixed left-1/2 top-1/2 z-50 max-h-[calc(100vh-2rem)] w-[min(32rem,calc(100vw-2rem))] -translate-x-1/2 -translate-y-1/2 overflow-y-auto rounded-lg bg-white p-6 shadow-card">
          {handover ? (
            <PasswordHandover created={handover} onDone={close} />
          ) : (
            <>
              <Dialog.Title className="text-h2 text-ink-900">Add an admin</Dialog.Title>
              <Dialog.Description className="mt-1 text-body text-ink-500">
                They get a password on the next screen, shown once. Send it to them over something
                private and ask them to change it.
              </Dialog.Description>

              <form onSubmit={submit} className="mt-4 space-y-4">
                <Field
                  label="Name"
                  name="name"
                  value={name}
                  onChange={(event) => {
                    setName(event.target.value);
                  }}
                  required
                />

                <Field
                  label="Email address"
                  name="email"
                  type="email"
                  value={email}
                  onChange={(event) => {
                    setEmail(event.target.value);
                  }}
                  required
                  hint="What they sign in with, and where a password reset would go."
                />

                <SelectField
                  label="Role"
                  value={role}
                  onChange={(event) => {
                    setRole(event.target.value as 'admin' | 'super_admin');
                  }}
                  options={[
                    { value: 'admin', label: 'Admin — everything except the four locked things' },
                    {
                      value: 'super_admin',
                      label: 'Super admin — everything, including these accounts',
                    },
                  ]}
                />

                {apiError ? <FormError>{apiError.message}</FormError> : null}

                <div className="flex justify-end gap-2">
                  <Button type="button" variant="outline" onClick={close}>
                    Cancel
                  </Button>
                  <Button type="submit" disabled={create.isPending}>
                    {create.isPending ? 'Creating…' : 'Create admin'}
                  </Button>
                </div>
              </form>
            </>
          )}
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

/** The same shape the employee dialog uses — shown once, never stored readably. */
function PasswordHandover({
  created,
  onDone,
}: {
  created: CreatedAdminAccount;
  onDone: () => void;
}) {
  const [copied, setCopied] = useState(false);

  return (
    <>
      <Dialog.Title className="text-h2 text-ink-900">{created.account.name} is set up</Dialog.Title>
      <Dialog.Description className="mt-1 text-body text-ink-500">
        Give them these details over something private. The password is shown once and is not stored
        anywhere it can be read back.
      </Dialog.Description>

      <dl className="mt-5 space-y-3 rounded-sm border border-ink-300 bg-ink-100/40 p-4">
        <div>
          <dt className="text-meta text-ink-500">They sign in with</dt>
          <dd className="text-body text-ink-900">
            {created.account.email ?? created.account.username}
          </dd>
        </div>
        <div>
          <dt className="text-meta text-ink-500">Temporary password</dt>
          <dd className="mt-1 flex flex-wrap items-center gap-2">
            <code className="min-w-0 break-all rounded-sm border border-ink-300 bg-white px-2 py-1.5 text-body">
              {created.temporaryPassword}
            </code>
            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                void navigator.clipboard.writeText(created.temporaryPassword).then(() => {
                  setCopied(true);
                });
              }}
            >
              {copied ? <Check aria-hidden="true" /> : <Copy aria-hidden="true" />}
              {copied ? 'Copied' : 'Copy'}
            </Button>
          </dd>
        </div>
      </dl>

      <div className="mt-6 flex justify-end">
        <Button onClick={onDone}>I have taken a copy</Button>
      </div>
    </>
  );
}
